import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FaqCO, SchoolCO } from 'src/app/shared/models/ApiModels';
import { ApiService } from 'src/app/shared/services/api.service';
import { SchoolDetailsService } from 'src/app/shared/services/school-details.service';
import { ElementRef, ViewChild } from '@angular/core';

interface GroupedFaqs {
  categoryId: number;
  categoryName: string;
  faqs: FaqCO[];
}

@Component({
  selector: 'app-home-faq',
  templateUrl: './home-faq.component.html',
  styleUrls: ['./home-faq.component.scss']
})
export class HomeFaqComponent implements OnInit, OnDestroy {

  school: SchoolCO | null = null;
  faqList: FaqCO[] = [];
  groupedFaqs: GroupedFaqs[] = [];

  panelOpenState = false;
  private destroy$ = new Subject<void>();

  @ViewChild('faqScroll') faqScroll!: ElementRef;

  constructor(
    private router: Router,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private schoolService: SchoolDetailsService
  ) {}

  ngOnInit(): void {
    this.getSchoolDetails();
  }

  private getSchoolDetails(): void {
    this.schoolService.schoolState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (state.error) {
          this.showError(state.error);
          return;
        }

        if (!state.data) {
          this.school = null;
          return;
        }

        this.school = state.data;
        this.getFaqList();
      });
  }

  private getFaqList(): void {
    if (!this.school?.id) return;

    this.apiService
      .getWebFaqListByStatus('ACTIVE', this.school.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.faqList = res?.data ?? [];
          this.groupFaqsByCategory();
        },
        error: () => {
          this.showError('Failed to load FAQs');
        }
      });
  }

  private groupFaqsByCategory(): void {
    const map = new Map<number, GroupedFaqs>();

    this.faqList.forEach(faq => {
      if (!faq.category) return;

      if (!map.has(faq.category.id)) {
        map.set(faq.category.id, {
          categoryId: faq.category.id,
          categoryName: faq.category.categoryName,
          faqs: []
        });
      }

      map.get(faq.category.id)!.faqs.push(faq);
    });

    this.groupedFaqs = Array.from(map.values());
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'x', { duration: 3000 });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByCategory(_: number, group: any) {
    return group.categoryName;
  }

  trackByFaq(_: number, faq: any) {
    return faq.id ?? faq.question;
  }

  hasScrolled = false;

  onScroll() {
    this.hasScrolled = true;
  }

  scrollToCategory(id: number) {
    const container = this.faqScroll.nativeElement;
    const element = document.getElementById('faq-' + id);

    if (container && element) {
      container.scrollTo({
        top: element.offsetTop - 10,
        behavior: 'smooth'
      });
    }
  }

}
